import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { Header } from "@/components/site/Header";
import { SectionHeading, Reveal } from "@/components/site/Primitives";
import { OUR_WORK, CONTACT } from "@/data/content";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function OurWork() {
  const navigate = useNavigate();
  const categories = useMemo(() => ["All", ...new Set(OUR_WORK.map((i) => i.category))], []);
  const [cat, setCat] = useState("All");
  const items = useMemo(
    () => (cat === "All" ? OUR_WORK : OUR_WORK.filter((i) => i.category === cat)),
    [cat]
  );
  const [lb, setLb] = useState(null);

  useEffect(() => {
    document.title = "Our Work — Perfect Touch Detailed Cleaning & Logistics Services";
    window.scrollTo(0, 0);
    return () => {
      document.title = "Perfect Touch Detailed Cleaning & Logistics Services | Queens, Brooklyn & Manhattan NYC";
    };
  }, []);

  useEffect(() => {
    if (lb === null) return undefined;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setLb(null);
      if (e.key === "ArrowRight") setLb((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setLb((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lb, items.length]);

  return (
    <div data-testid="ourwork-page" className="min-h-screen bg-[#050505] text-white pt-grain">
      <Header />

      <section className="pt-28 md:pt-40 pb-14 px-6 md:px-10 mx-auto max-w-[1400px]">
        <Reveal>
          <SectionHeading
            overline="Our Work"
            title={<>Real projects. <span className="gold-gradient italic">Real results.</span></>}
            subtitle="A look inside recent Perfect Touch visits — photographed on site by our team, exactly as we found each space and as we left it."
          />
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2.5" role="tablist" aria-label="Project categories">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`ourwork-filter-${slug(c)}`}
                onClick={() => { setCat(c); setLb(null); }}
                aria-pressed={cat === c}
                className={`rounded-full px-5 py-2.5 text-[13px] font-medium transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-gold/60 ${
                  cat === c
                    ? "bg-gold text-black"
                    : "border border-white/15 text-zinc-300 hover:border-gold hover:text-gold"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="ourwork-grid">
          {items.map((it, i) => (
            <Reveal key={it.src} delay={(i % 3) * 0.07}>
              <button
                data-testid={`ourwork-item-${slug(it.category)}`}
                onClick={() => setLb(i)}
                className="group block w-full text-left border border-white/5 bg-[#121212] overflow-hidden hover:border-gold transition-colors duration-300"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-black">
                  <img
                    src={it.src}
                    alt={`${it.category} project by Perfect Touch`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between gap-3">
                    <span className="overline">{it.category}</span>
                    {it.pair && (
                      <span className="shrink-0 text-[9px] font-semibold tracking-[0.18em] uppercase text-gold border border-gold/40 rounded-full px-2.5 py-1">
                        Before &amp; After
                      </span>
                    )}
                  </div>
                  <p className="mt-2.5 text-sm text-zinc-400 leading-relaxed">{it.caption}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-10 pb-20">
        <Reveal className="mx-auto max-w-[1400px]">
          <div className="border border-gold/25 bg-[#0d0d0d] px-6 py-12 md:p-16 text-center">
            <h2 className="font-display font-light text-3xl sm:text-4xl tracking-tight">
              Want results <span className="gold-gradient italic">like these?</span>
            </h2>
            <p className="mt-4 text-zinc-400 text-base md:text-lg max-w-xl mx-auto">
              Tell us about your space and we&apos;ll take care of the rest — detailed, reliable, and always the Perfect Touch.
            </p>
            <button
              data-testid="ourwork-cta-quote"
              onClick={() => navigate("/#quote")}
              className="mt-8 rounded-full bg-gold text-black font-semibold px-8 py-4 hover:bg-[#c5a059] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-gold/60"
            >
              Request a Free Quote
            </button>
          </div>
        </Reveal>
      </section>

      <footer className="border-t border-white/10 px-6 md:px-10 py-8">
        <div className="mx-auto max-w-[1400px] flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
          <span>Perfect Touch Detailed Cleaning &amp; Logistics Services</span>
          <a
            href={CONTACT.phoneHref}
            data-testid="ourwork-phone"
            className="flex items-center gap-2 hover:text-gold transition-colors duration-300"
          >
            <Phone size={14} className="text-gold" />
            {CONTACT.phone}
          </a>
        </div>
      </footer>

      <AnimatePresence>
        {lb !== null && items[lb] && (
          <motion.div
            data-testid="ourwork-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] bg-black/95 backdrop-blur flex flex-col items-center justify-center p-4 md:p-10"
            onClick={() => setLb(null)}
            role="dialog"
            aria-modal="true"
            aria-label={`${items[lb].category} photo viewer`}
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setLb(null)}
              aria-label="Close viewer"
              className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 flex items-center justify-center rounded-full border border-white/20 text-white hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <X size={20} />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); setLb((i) => (i - 1 + items.length) % items.length); }}
              aria-label="Previous photo"
              className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full border border-white/20 text-white hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); setLb((i) => (i + 1) % items.length); }}
              aria-label="Next photo"
              className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full border border-white/20 text-white hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <ChevronRight size={22} />
            </button>
            <img
              src={items[lb].src}
              alt={`${items[lb].category} project by Perfect Touch — enlarged`}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[76vh] max-w-full object-contain"
            />
            <div className="mt-5 text-center" onClick={(e) => e.stopPropagation()}>
              <span className="overline">{items[lb].category}</span>
              <p className="mt-2 text-sm text-zinc-400 max-w-lg">{items[lb].caption}</p>
              <p className="mt-2 text-xs text-zinc-600">{lb + 1} of {items.length}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
